<script setup lang="ts">
import SmallDropdown from "~/components/Common/Dropdowns/SmallDropdown.vue";
import UserIcon from "~/components/Header/UserIcon.vue";
import AccountDropdown from "~/components/Header/AccountDropdown.vue";
import { useAlerts } from "~/composables/alerts/useAlerts";
import SvgButton from "../Common/Buttons/SvgButton.vue";
import NotificationBadge from "../Common/NotificationBadge.vue";
import AlertCenterPanel from "../Alerts/AlertCenterPanel.vue";
import { useAlertsStore } from "~/store/alerts/alerts.store";
import { useAuthStore } from "~/store/auth/auth.store";

/* The four triggers of the app header and the two panels they open. */
const authStore = useAuthStore();
const alertsStore = useAlertsStore();
const { hasActiveAlerts } = storeToRefs(alertsStore);
const { getGlobalCallbacks } = useAlerts();
const globalCallbacks = getGlobalCallbacks();
const { user } = storeToRefs(authStore);
const showAccount = ref<boolean>(false);
const route = useRoute();

/*
 * The signed-in actions — alerts bell, separator, avatar, account menu — must stay off a page
 * that declares itself public (`definePageMeta({ isLoggedIn: false })`): the login funnel and the
 * SSO callbacks, all on the guest layout. `setTokens()` flips isLoggedIn() the moment the password
 * check returns, which is ~120 ms before the router leaves "/" on a built app and several seconds
 * on a cold dev server — so the avatar used to light up over a login page that had not moved yet,
 * reading as "you're in" while nothing had happened. Same meta the auth middleware keys off.
 */
const showAuthenticatedActions = computed(() => authStore.isLoggedIn() && route.meta.isLoggedIn !== false);

declare global {
  interface Window {
    Beacon: any;
  }
}

function openHelpscout() {
  window.Beacon("open");
}

const eventToggleOffDropdown = (event: FocusEvent) => {
  if (event?.relatedTarget instanceof HTMLElement) {
    if (event.relatedTarget.classList.contains("disconnect_button")) {
      return;
    }
  }
  showAccount.value = false;
};
</script>

<template>
  <div class="header-actions flex gap-[16px] items-center">
    <NotificationBadge
      v-if="showAuthenticatedActions"
      :show="hasActiveAlerts"
      :count="alertsStore.activeAlertsCount"
      color="var(--red-main)"
    >
      <SvgButton :width="18" :height="18" class="button-icon" name="bell" @click="alertsStore.togglePanel()" />
    </NotificationBadge>
    <SvgButton :width="18" :height="16" class="faq-button" name="tooltip" @click="openHelpscout"></SvgButton>
    <SmallDropdown></SmallDropdown>
    <span v-if="showAuthenticatedActions" class="w-px h-5 bg-stroke-color shrink-0" />
    <div v-if="showAuthenticatedActions" class="relative shrink-0">
      <UserIcon
        ref="user_icon"
        :initials="
          user?.firstname && user?.lastname ? user?.firstname?.charAt(0) + user?.lastname?.charAt(0) : undefined
        "
        @click="showAccount = !showAccount"
        @focusout="eventToggleOffDropdown"
      >
      </UserIcon>
      <AccountDropdown :showAccount="showAccount" top-offset="19px" @close="showAccount = false"></AccountDropdown>
    </div>
  </div>
  <AlertCenterPanel v-if="alertsStore.isGlobalPanel" :callbacks="globalCallbacks" />
</template>

<style scoped lang="scss">
.header-actions {
  :deep(.button-icon) {
    @apply text-secondary-color cursor-pointer border-none h-auto w-auto bg-transparent;

    &:hover {
      @apply text-primary-color bg-transparent;
    }
  }

  :deep(.faq-button) {
    @apply text-primary-blue-color cursor-pointer border-none h-auto w-auto bg-transparent;

    &:hover {
      @apply text-blue-dark bg-lightest-grey-color;
    }
  }
}
</style>
